import * as React from 'react';
import { Button, Space, Tag } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useDimensions, useAggregations, usePatternQuery } from '@src/services/queries';
import FunnelDimensionTable from '@shared/components/FunnelDimensionTable';
import { PropagationTag } from '@shared/components/PropagationDisplay';
import { FULL_STEP_AGGREGATION_REPLACE_MAP } from '@src/shared/constants/analysis';
import Control from '@src/icons/control.svg';
import CustomizePopup from '../CustomizePopup';
import * as store from '../../store';
import * as utils from '../../utils';
import * as Styles from './styled';

export interface DimensionTableProps {
  dimension: string;
  aggregations: string[];
  propagation: Propagation;
  sort?: FunnelAnalyses['dimension_analyses'][0]['order_by'];
}

const DimensionTable = (props: DimensionTableProps) => {
  const { dimension, aggregations, propagation, sort } = props;
  const onChange = store.useOnChange();
  const queryAggregations = React.useMemo(
    () =>
      aggregations
        .map((it) =>
          it in FULL_STEP_AGGREGATION_REPLACE_MAP
            ? [FULL_STEP_AGGREGATION_REPLACE_MAP[it], it]
            : it,
        )
        .flat(2),
    [aggregations],
  );
  const [basePayload, { pattern }] = utils.usePatternQueryPayload();
  const aggregationsMeta = useAggregations().data?.aggregations;
  const { data: dimensions } = useDimensions();
  const currentAggregations = React.useMemo(
    () =>
      aggregationsMeta?.filter((it) => it.type === 'pattern' && aggregations.includes(it.id)) ?? [],
    [aggregationsMeta, aggregations],
  );

  const handleRemove = React.useCallback(() => {
    onChange((draft) => {
      const index = draft.dimension_analyses.findIndex((it) => it.dimension === dimension);
      if (index !== -1) {
        draft.dimension_analyses.splice(index, 1);
      }
    });
  }, [onChange, dimension]);

  const handleDimensionValueClick = React.useCallback<
    GetProp<typeof FunnelDimensionTable, 'onDimensionValueClick'>
  >(
    (value) => {
      onChange((draft) => {
        if (!Array.isArray(draft.filters)) {
          draft.filters = [];
        }

        draft.filters.push([
          {
            field: value.dimension,
            op: 'equals',
            val: value.value,
            label: value.label,
          },
        ]);
      });
    },
    [onChange],
  );

  const handleSort = React.useCallback<GetProp<typeof FunnelDimensionTable, 'onSort'>>(
    (sort) => {
      onChange((draft) => {
        const index = draft.dimension_analyses.findIndex((it) => it.dimension === dimension);
        if (index !== -1) {
          draft.dimension_analyses[index].order_by = sort;
        }
      });
    },
    [onChange, dimension],
  );

  const query = usePatternQuery({
    ...basePayload,
    aggregations: queryAggregations,
    group_by: [
      {
        field: dimension,
        type: propagation,
      },
    ],
    order_by: sort
      ? [
          {
            field: `${sort.step === 'full' ? `step_${pattern.length}` : `step_${sort.step}`}.${sort.field}`,
            order: sort.order,
          },
        ]
      : undefined,
  });

  // Only re-compute when data changes to avoid chart re-rendering during loading state
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentPattern = React.useMemo(() => pattern, [query.data]);

  const isValidDimension = !dimensions || !!dimensions.dimensionMap.get(dimension);

  return (
    <Styles.TableContainer>
      <FunnelDimensionTable
        loading={query.isFetching}
        events={currentPattern}
        aggregations={currentAggregations}
        dimension={{
          id: dimension,
          name: isValidDimension ? (
            <Space size={isValidDimension ? 2 : 10} align="baseline">
              {dimensions?.dimensionMap.get(dimension)?.name ?? dimension}
              <PropagationTag>{propagation}</PropagationTag>
            </Space>
          ) : (
            <Tag color="red">Dimension Deleted</Tag>
          ),
        }}
        data={query.data}
        sort={sort}
        control={
          <CustomizePopup
            mode="edit"
            defaultDimensionValue={dimension}
            defaultMetricsValue={aggregations}
          >
            <Button size="small" color="primary" variant="text" icon={<Control />} />
          </CustomizePopup>
        }
        onDimensionValueClick={handleDimensionValueClick}
        onSort={handleSort}
      />
      <Button
        loading={query.isFetching}
        size="small"
        color="default"
        variant="filled"
        icon={<CloseOutlined />}
        className="remove"
        onClick={handleRemove}
      />
    </Styles.TableContainer>
  );
};

export default React.memo(DimensionTable);
